/**
 * AutoRenewConfirmModal
 *
 * Records the player's intent to auto-renew their training program.
 * No payment is taken now — the card is saved on file and charged only after
 * the last lesson of the current program is delivered.
 * The player can cancel this intent at any time before that point at no charge.
 */

import { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatPrice } from '../utils/formatPrice';
import { BETA_MODE } from '../utils/betaMode';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise, createSetupIntentFn, listPaymentMethodsFn } from '../services/stripe';
import type { SavedCard } from '../services/stripe';
import { markOrderAutoRenewOptedIn } from '../services/orders';
import type { Order } from '../types/order';
import { useUserIdentity } from '../hooks/useUserIdentity';

// ── Card-setup section (saves card via SetupIntent, no charge) ────────────────

interface CardSetupHandle {
  saveCard: () => Promise<void>;
}

interface CardSetupInnerProps {
  disabled: boolean;
}

const CardSetupInner = forwardRef<CardSetupHandle, CardSetupInnerProps>(({ disabled }, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState('');

  useImperativeHandle(ref, () => ({
    async saveCard() {
      if (!stripe || !elements) throw new Error('Stripe not ready');
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not mounted');
      const { data } = await createSetupIntentFn();
      if (!data.clientSecret) throw new Error('No client secret returned');
      const result = await stripe.confirmCardSetup(data.clientSecret, {
        payment_method: { card: cardElement },
      });
      if (result.error) throw new Error(result.error.message ?? 'Card setup failed');
    },
  }));

  return (
    <div>
      <div className="text-[13px] font-bold text-db-text-1 mb-2">Save a card for auto-renewal</div>
      <div className="border border-db-border rounded-xl overflow-hidden mb-2">
        <div className={`px-4 py-3.5 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '14px',
                  color: '#111827',
                  fontFamily: 'system-ui, sans-serif',
                  '::placeholder': { color: '#9AABB8' },
                },
                invalid: { color: '#DC2626' },
              },
              hidePostalCode: true,
            }}
            onChange={(e) => setCardError(e.error?.message ?? '')}
          />
        </div>
        {cardError && (
          <div className="px-4 pb-3 text-[12px] text-red-600">{cardError}</div>
        )}
      </div>
      <p className="text-[11px] text-db-text-3 text-center">
        Secured by Stripe · Your card is saved but not charged today
      </p>
    </div>
  );
});
CardSetupInner.displayName = 'AutoRenewCardSetup';

// ── Main modal ────────────────────────────────────────────────────────────────

interface AutoRenewConfirmModalProps {
  order: Order;
  onClose: () => void;
  onConfirmed: () => void;
}

function AutoRenewConfirmModalInner({ order, onClose, onConfirmed }: AutoRenewConfirmModalProps) {
  const { currentUser } = useAuth();
  const cardSetupRef = useRef<CardSetupHandle | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedCard, setSavedCard] = useState<SavedCard | null>(null);
  const [loadingCards, setLoadingCards] = useState(!BETA_MODE);

  useEffect(() => {
    if (BETA_MODE) return;
    listPaymentMethodsFn()
      .then(({ data }) => {
        const def = data.paymentMethods.find((c) => c.isDefault) ?? data.paymentMethods[0] ?? null;
        setSavedCard(def);
      })
      .catch(() => { /* treat as no saved card */ })
      .finally(() => setLoadingCards(false));
  }, []);

  const liveCoach = useUserIdentity(order.coachId, 'coach');
  const coachFirstName = (liveCoach?.name ?? order.coachName)?.split(' ')[0] || 'your coach';
  const totalLessons = order.totalLessons ?? 4;
  const priceCents = order.priceCents ?? 0;
  const totalChargeCents = priceCents * totalLessons;

  // True when we need to show the card-entry form (no card saved yet)
  const needsCardSetup = !BETA_MODE && !loadingCards && !savedCard;

  const handleConfirm = async () => {
    if (!currentUser) return;
    setSubmitting(true);
    setError(null);

    try {
      // If the player has no card saved, collect and save one via SetupIntent first.
      if (needsCardSetup && cardSetupRef.current) {
        await cardSetupRef.current.saveCard();
      }

      // Record the intent — card will be charged after last lesson delivery.
      await markOrderAutoRenewOptedIn(order.id);
      onConfirmed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9000] flex items-end sm:items-center justify-center px-0 sm:px-4 backdrop-fade"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div className="bg-white rounded-t-[20px] sm:rounded-[20px] w-full sm:max-w-[480px] shadow-xl overflow-hidden scale-pop">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-db-border flex items-start justify-between gap-4">
          <div>
            {/* The subtitle names when, so "wraps up" goes: a billing decision
                should not be dated by a phrase that could mean either the last
                delivery or some point after it. */}
            <div className="text-[18px] font-extrabold text-biscayne mb-0.5">
              Auto renew your training program
            </div>
            <div className="text-[13px] text-db-text-2">
              Start another {totalLessons} lessons with{' '}
              <strong className="text-db-text-1">{coachFirstName}</strong>{' '}
              once your last lesson is delivered.
            </div>
          </div>
          {!submitting && (
            <button
              onClick={onClose}
              className="text-db-text-3 hover:text-db-text-2 transition-colors flex-shrink-0 mt-0.5"
              aria-label="Close"
            >
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">

          {/* How it works */}
          <div className="bg-db-bg rounded-xl px-4 py-4">
            <div className="text-[12px] font-bold text-db-text-1 mb-2.5 uppercase tracking-[.06em]">
              How it works
            </div>
            <div className="flex flex-col gap-2">
              {/* Three lines, three facts, each said once: what happens now,
                  what arrives, how to stop it. The charge is NOT here — it is
                  stated under the card, where the card being charged is, and
                  repeating it was the modal's loudest duplication.
                  "Lessons" is the tab's real name; "My Lessons" was not. */}
              {[
                'Nothing is charged today',
                `Your next ${totalLessons} lessons appear in Lessons once the charge goes through`,
                'Cancel any time before your last lesson is delivered',
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#0A1628" strokeWidth="2.5" className="flex-shrink-0 mt-0.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span className="text-[13px] text-db-text-2">{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Card section */}
          {BETA_MODE ? (
            <div className="px-4 py-3.5 bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl flex items-center gap-3">
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#059669" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span className="text-[13px] font-semibold text-[#065F46]">Beta · No card required</span>
            </div>
          ) : loadingCards ? (
            <div className="h-14 flex items-center justify-center">
              <svg className="animate-spin w-5 h-5 text-db-text-3" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : savedCard ? (
            <div>
              <div className="text-[13px] font-bold text-db-text-1 mb-2">Card on file</div>
              <div className="border border-db-border rounded-xl px-4 py-3.5 flex items-center gap-2.5">
                <span className="text-[13px] font-semibold text-db-text-1 capitalize">{savedCard.brand}</span>
                <span className="text-[13px] text-db-text-2">····{savedCard.last4}</span>
                <span className="text-[12px] text-db-text-3">{savedCard.expMonth}/{savedCard.expYear}</span>
              </div>
            </div>
          ) : (
            <Elements stripe={stripePromise}>
              <CardSetupInner ref={cardSetupRef} disabled={submitting} />
            </Elements>
          )}

          {/* The one place the charge is stated: the amount and when, beside
              the card it lands on. Outside the savedCard branch deliberately —
              in there it never reached a player who has no card on file yet,
              which is the player most owed the number. Not in beta, where the
              banner above says no card is required and nothing is charged. */}
          {!BETA_MODE && (
            <p className="text-[11px] text-db-text-3 text-center">
              Charged {formatPrice(totalChargeCents)} after your last lesson is delivered
            </p>
          )}

          {/* Error */}
          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-[13px] text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 space-y-2">
          <button
            data-testid="confirm-auto-renew-btn"
            onClick={handleConfirm}
            disabled={submitting || loadingCards}
            className="w-full py-3.5 bg-db-accent rounded-xl text-[14px] font-bold text-white hover:bg-db-accent-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 btn-press"
          >
            {submitting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Confirming…
              </>
            ) : (
              'Confirm Auto Renew'
            )}
          </button>
          {!submitting && (
            <button
              onClick={onClose}
              className="w-full py-2.5 text-[13px] font-semibold text-db-text-2 hover:text-db-text-1 transition-colors"
            >
              Not Now
            </button>
          )}
        </div>

      </div>
    </div>
  );
}

export default function AutoRenewConfirmModal(props: AutoRenewConfirmModalProps) {
  return <AutoRenewConfirmModalInner {...props} />;
}
